import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Upload,
  ChevronDown as Chevron,
  Play,
} from 'lucide-react';
import { createMediaBlobUrl } from '@/utils/blobMediaType';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { FieldIssue } from '../../types';
import type { Campaign, ValidationFeedback, AssetOrderToken } from '@/api/campaigns';
import {
  CharCount,
  FieldLabel,
  InlineIssue,
  Field,
  inputClass,
  issueBorder,
  mergeIssues,
} from './shared';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';
import { seedSignature } from './seedSignature';
import { revokeCachedBlobUrls } from './revokeCachedBlobUrls';
import {
  SlotThumb,
  FEED_RATIOS,
  EMPTY_SLOT,
  emptySlots,
  isSlotFilled,
  type CardSlot,
  type FeedRatio,
} from './feedCardSlots';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY_TEXT_LIMIT = 125;
const HEADLINE_LIMIT = 40;
const DESCRIPTION_LIMIT = 25;
const MIN_CARDS = 2;
const MAX_CARDS = 10;

const CTA_OPTIONS = [
  'Apply Now',
  'Book Now',
  'Contact Us',
  'Get Offer',
  'Get Quote',
  'Learn More',
  'Order Now',
  'Shop Now',
  'Sign Up',
];

// ─── Types ────────────────────────────────────────────────────────────────────

// The slot model itself is shared with the LinkedIn carousel — see feedCardSlots.
// Only the card's own text fields differ between the two formats.
interface Card {
  id: number;
  slots: Record<FeedRatio, CardSlot>;
  headline: string;
  description: string;
}

let nextId = 1;

function makeNewCard(): Card {
  return { id: nextId++, slots: emptySlots(), headline: '', description: '' };
}

// ─── Card row sub-component ───────────────────────────────────────────────────

function CardRow({
  card,
  index,
  total,
  isActive,
  activeRatio,
  headlineIssue,
  cardImageIssue,
  descriptionIssue,
  showErrors,
  showWarnings,
  feedback,
  feedbackPending,
  onFeedback,
  onSelect,
  onSlotFileChange,
  onHeadlineChange,
  onDescriptionChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  card: Card;
  index: number;
  total: number;
  isActive: boolean;
  /** Which crop the mockup is currently showing, so the right thumb gets the ring. */
  activeRatio: FeedRatio;
  headlineIssue?: FieldIssue;
  /** Raised when the card has text but no image in either crop. */
  cardImageIssue?: FieldIssue;
  descriptionIssue?: FieldIssue;
  showErrors: boolean;
  showWarnings: boolean;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onSelect: (ratio: FeedRatio) => void;
  onSlotFileChange: (ratio: FeedRatio, f: File | null) => void;
  onHeadlineChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      className={`relative group/card flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
        isActive ? 'border-slate-200 bg-slate-50' : 'border-slate-100 hover:border-slate-200'
      }`}
      onClick={() => onSelect(activeRatio)}
    >
      {/* ── Delete — top-right of card, hover only ── */}
      <div
        className="absolute top-2 right-2 opacity-0 group-hover/card:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <button
                type="button"
                onClick={onRemove}
                disabled={total <= MIN_CARDS}
                className="w-6 h-6 rounded flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {total <= MIN_CARDS
              ? `Cannot delete — minimum ${MIN_CARDS} cards required`
              : 'Remove card'}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* ── Thumbnails: the card's two feed crops ── */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          {FEED_RATIOS.map((r) => (
            <SlotThumb
              key={r}
              slot={card.slots[r]}
              ratio={r}
              index={index}
              accept="image/*,video/*"
              isActive={isActive && activeRatio === r}
              onFileChange={(f) => onSlotFileChange(r, f)}
              onSelect={() => onSelect(r)}
            />
          ))}
        </div>
        <InlineIssue
          issue={cardImageIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onFeedback}
        />
      </div>

      {/* ── Text fields ── */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* Card label */}
        <span
          className={`text-[10px] font-bold tracking-wider ${isActive ? 'text-slate-600' : 'text-slate-400'}`}
        >
          CARD {index + 1}
        </span>

        {/* Headline and description belong to the CARD, not to a crop: 1:1 and
            4:5 are two renderings of the same card, so they share this text. */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={card.headline}
            onChange={(e) => onHeadlineChange(e.target.value)}
            placeholder="Card headline..."
            className={`${inputClass} text-xs py-1.5 pr-12 ${issueBorder(headlineIssue, showErrors, showWarnings)}`}
          />
          <span
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tabular-nums pointer-events-none ${
              card.headline.length > HEADLINE_LIMIT
                ? 'text-amber-600 font-medium'
                : 'text-slate-400'
            }`}
          >
            {card.headline.length}/{HEADLINE_LIMIT}
          </span>
        </div>
        <InlineIssue
          issue={headlineIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onFeedback}
        />

        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={card.description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Description (optional)"
            className={`${inputClass} text-xs py-1.5 pr-12 ${issueBorder(descriptionIssue, showErrors, showWarnings)}`}
          />
          <span
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tabular-nums pointer-events-none ${
              card.description.length > DESCRIPTION_LIMIT
                ? 'text-amber-600 font-medium'
                : 'text-slate-400'
            }`}
          >
            {card.description.length}/{DESCRIPTION_LIMIT}
          </span>
        </div>
        <InlineIssue
          issue={descriptionIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onFeedback}
        />
      </div>

      {/* ── Reorder arrows — vertically centred ── */}
      <div
        className="flex flex-col gap-1 flex-shrink-0 self-center"
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <button
                type="button"
                onClick={onMoveUp}
                disabled={index === 0}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">
            {index === 0 ? 'Cannot move up — already first in order' : 'Move up'}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={index === total - 1}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">
            {index === total - 1 ? 'Cannot move down — already last in order' : 'Move down'}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ─── Collapsible card section (self-contained with persistence + inline errors) ─

function CardSection({
  label,
  activeCardIndex,
  activeRatio,
  fieldIssues,
  showErrors,
  showWarnings,
  sectionIssueKey,
  feedback,
  feedbackPending,
  onFeedback,
  onItemsChange,
  onCardsChange,
  onSelectCard,
  initialItems,
  injectedFiles,
  injectedFiles4x5,
}: {
  label: string;
  activeCardIndex: number;
  /** Which feed crop the mockup is showing. */
  activeRatio: FeedRatio;
  fieldIssues: Record<string, FieldIssue>;
  showErrors: boolean;
  showWarnings: boolean;
  /** Key(s) for the section-level count validation. An array is merged, which is
      how the "1:1 or 4:5 missing" pair issue lights up the whole section. */
  sectionIssueKey: string | string[];
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onItemsChange: (
    items: {
      imageUrl?: string;
      imageUrl4x5?: string;
      contentType?: string | null;
      contentType4x5?: string | null;
      headline?: string;
      description?: string;
    }[],
  ) => void;
  /** Per-ratio upload payload. Each crop uploads and orders independently, but
      cardData/totalCount describe the shared card list. */
  onCardsChange: (
    perRatio: Record<FeedRatio, { newFiles: File[]; order: AssetOrderToken[] }>,
    deletedIds: string[],
    totalCount: number,
    cardData: { headline: string; description: string }[],
  ) => void;
  onSelectCard: (index: number, ratio: FeedRatio) => void;
  initialItems?: {
    imageUrl?: string;
    existingAssetId?: string;
    contentType?: string | null;
    imageUrl4x5?: string;
    existingAssetId4x5?: string;
    contentType4x5?: string | null;
    headline: string;
    description: string;
  }[];
  /** Files injected from outside (e.g., GX import). Added to cards when changed. */
  injectedFiles?: File[];
  /** Same, for the 4:5 crop — fills each card's 4:5 slot rather than its 1:1. */
  injectedFiles4x5?: File[];
}) {
  const [open, setOpen] = useState(true);
  const [cards, setCards] = useState<Card[]>([makeNewCard(), makeNewCard()]);
  // Identity of the initial placeholder array, used to detect the pre-seed render.
  const initialCardsRef = useRef(cards);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const bulkInputRef = useRef<HTMLInputElement>(null);

  // Accumulates existingAssetIds deleted this session (for server-side deletion on save)
  const deletedIdsRef = useRef<string[]>([]);
  // Signature of the initialItems last seeded from — see seedSignature.ts.
  const seededSigRef = useRef<string | null>(null);
  // Cache blob URLs so notify() returns the same URL on every call (prevents flicker).
  // Keyed by `${cardId}:${ratio}` because a card now has two independent previews.
  const blobUrlCacheRef = useRef<Map<string, string>>(new Map());
  // Ref that always holds the latest cards so notify() reads fresh data without depending on cards identity
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  // Ref for initialItems so notify can check seeding status without depending on prop identity
  const initialItemsRef = useRef(initialItems);
  initialItemsRef.current = initialItems;

  // Seed (and re-seed) cards from the DB whenever the incoming asset identities change
  useEffect(() => {
    const sig = seedSignature(initialItems);
    if (!sig || sig === seededSigRef.current) return;
    seededSigRef.current = sig;
    // A re-seed adopts server state wholesale, so pending deletions from the
    // previous seed no longer apply — they are already reflected in initialItems.
    deletedIdsRef.current = [];
    // Previous local previews are dropped wholesale by the re-seed below; their
    // cached blob URLs are no longer reachable for per-card cleanup.
    revokeCachedBlobUrls(blobUrlCacheRef.current);
    setCards(
      initialItems!.map((item) => ({
        id: nextId++,
        slots: {
          '1x1':
            item.imageUrl && item.existingAssetId
              ? {
                  kind: 'existing' as const,
                  existingAssetId: item.existingAssetId,
                  previewUrl: item.imageUrl,
                  contentType: item.contentType,
                }
              : EMPTY_SLOT,
          '4x5':
            item.imageUrl4x5 && item.existingAssetId4x5
              ? {
                  kind: 'existing' as const,
                  existingAssetId: item.existingAssetId4x5,
                  previewUrl: item.imageUrl4x5,
                  contentType: item.contentType4x5,
                }
              : EMPTY_SLOT,
        },
        headline: item.headline,
        description: item.description,
      })),
    );
  }, [initialItems]);

  // Notify parent whenever cards change — feeds both mockup display and save payload.
  // Uses cardsRef so the callback identity stays stable (only changes when parent callbacks change).
  // Skips onCardsChange when initialItems are expected but not yet seeded, to prevent
  // empty default cards from overwriting the parent's card data after a formKey remount.
  const notify = useCallback(() => {
    const currentCards = cardsRef.current;

    // Resolve one crop to a preview URL, caching blob URLs so repeated notify()
    // calls hand back the same URL (a fresh one each time makes the mockup flicker).
    const previewOf = (card: Card, r: FeedRatio): { url?: string; contentType?: string | null } => {
      const slot = card.slots[r];
      if (slot.kind === 'existing') return { url: slot.previewUrl, contentType: slot.contentType };
      if (slot.kind === 'empty') return { url: undefined, contentType: undefined };
      const key = `${card.id}:${r}`;
      if (!blobUrlCacheRef.current.has(key)) {
        blobUrlCacheRef.current.set(key, createMediaBlobUrl(slot.file));
      }
      return { url: blobUrlCacheRef.current.get(key), contentType: slot.file.type };
    };

    onItemsChange(
      currentCards.map((c) => {
        const feed = previewOf(c, '1x1');
        const alt = previewOf(c, '4x5');
        return {
          imageUrl: feed.url,
          contentType: feed.contentType,
          imageUrl4x5: alt.url,
          contentType4x5: alt.contentType,
          headline: c.headline,
          description: c.description,
        };
      }),
    );
    // Don't push empty defaults to parent while waiting for DB cards to seed.
    // Compare the cards array by identity rather than checking the seed
    // signature: on mount the seeding effect runs first and records the
    // signature synchronously, but the setCards it schedules hasn't been applied
    // yet — so the component looks seeded while currentCards is still the
    // placeholder array.
    if (initialItemsRef.current?.length && currentCards === initialCardsRef.current) return;

    // One entry per card, in card order, for each ratio — empties included. The
    // index in this array IS the card index, which is what lets a card's two crops
    // find each other again on reload. Compacting it would save card 3's lone 4:5
    // at position 0 and reattach it to card 1.
    const perRatio = {} as Record<FeedRatio, { newFiles: File[]; order: AssetOrderToken[] }>;
    for (const r of FEED_RATIOS) {
      const newFiles: File[] = [];
      const order: AssetOrderToken[] = [];
      for (const c of currentCards) {
        const slot = c.slots[r];
        if (slot.kind === 'existing') order.push({ existingAssetId: slot.existingAssetId });
        else if (slot.kind === 'new') {
          order.push({ newFileIndex: newFiles.length });
          newFiles.push(slot.file);
        } else {
          order.push({ empty: true });
        }
      }
      perRatio[r] = { newFiles, order };
    }

    onCardsChange(
      perRatio,
      deletedIdsRef.current,
      currentCards.length,
      currentCards.map((c) => ({
        headline: c.headline,
        description: c.description,
      })),
    );
  }, [onItemsChange, onCardsChange]);

  // Re-notify parent whenever cards state changes (notify reads fresh data via cardsRef)
  useEffect(() => {
    notify();
  }, [cards, notify]);

  // Replace (or clear) one crop of one card. The other crop is untouched — that
  // independence is the whole point of the per-slot model.
  function handleSlotFileChange(card: Card, r: FeedRatio, f: File | null) {
    const key = `${card.id}:${r}`;
    const cached = blobUrlCacheRef.current.get(key);
    if (cached) {
      URL.revokeObjectURL(cached);
      blobUrlCacheRef.current.delete(key);
    }
    const slot = card.slots[r];
    // Overwriting or clearing a saved crop means that row must be deleted server-side.
    if (slot.kind === 'existing' && !deletedIdsRef.current.includes(slot.existingAssetId)) {
      deletedIdsRef.current = [...deletedIdsRef.current, slot.existingAssetId];
    }
    const next: CardSlot = f ? { kind: 'new', file: f } : EMPTY_SLOT;
    setCards((prev) =>
      prev.map((c) => (c.id === card.id ? { ...c, slots: { ...c.slots, [r]: next } } : c)),
    );
  }

  function updateCard(id: number, patch: Partial<Pick<Card, 'headline' | 'description'>>) {
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }

  function removeCard(card: Card) {
    if (cards.length <= MIN_CARDS) return;
    // Removing a card removes BOTH its crops.
    for (const r of FEED_RATIOS) {
      const slot = card.slots[r];
      if (slot.kind === 'existing' && !deletedIdsRef.current.includes(slot.existingAssetId)) {
        deletedIdsRef.current = [...deletedIdsRef.current, slot.existingAssetId];
      }
      const key = `${card.id}:${r}`;
      const cached = blobUrlCacheRef.current.get(key);
      if (cached) {
        URL.revokeObjectURL(cached);
        blobUrlCacheRef.current.delete(key);
      }
    }
    setCards((prev) => prev.filter((c) => c.id !== card.id));
  }

  function moveCard(index: number, direction: -1 | 1) {
    const next = [...cards];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setCards(next);
  }

  // Bulk drop/pick fills the 1:1 crop only. 4:5 is an alternative rendering of a
  // card, so guessing which dropped file is the 4:5 of which card is not
  // something we can infer — those are picked per slot.
  function addBulkFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    setCards((prev) => {
      const next = [...prev];
      let fileIdx = 0;
      // Fill cards with an empty 1:1 first
      for (let i = 0; i < next.length && fileIdx < files.length; i++) {
        if (!isSlotFilled(next[i].slots['1x1'])) {
          next[i] = {
            ...next[i],
            slots: { ...next[i].slots, '1x1': { kind: 'new', file: files[fileIdx++] } },
          };
        }
      }
      // Create new cards for remaining files
      while (fileIdx < files.length && next.length < MAX_CARDS) {
        next.push({
          id: nextId++,
          slots: { '1x1': { kind: 'new', file: files[fileIdx++] }, '4x5': EMPTY_SLOT },
          headline: '',
          description: '',
        });
      }
      return next;
    });
  }

  useEffect(() => {
    if (injectedFiles && injectedFiles.length > 0) addBulkFiles(injectedFiles);
  }, [injectedFiles]); // addBulkFiles is stable (no deps that change)

  /**
   * Fills the 4:5 crop, for GX imports only.
   *
   * Drag/drop can't say which dropped file is the 4:5 of which card, so it fills
   * 1:1 only. Gear Exchange does know: the modal detects a true 4:5 and routes it
   * here. Empty 4:5 slots on existing cards go first, so a mixed import lands
   * both crops on the same card; leftovers open new cards, which validation then
   * flags for a missing 1:1.
   */
  function addBulkFiles4x5(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    setCards((prev) => {
      const next = [...prev];
      let fileIdx = 0;
      for (let i = 0; i < next.length && fileIdx < files.length; i++) {
        if (!isSlotFilled(next[i].slots['4x5'])) {
          next[i] = {
            ...next[i],
            slots: { ...next[i].slots, '4x5': { kind: 'new', file: files[fileIdx++] } },
          };
        }
      }
      while (fileIdx < files.length && next.length < MAX_CARDS) {
        next.push({
          id: nextId++,
          slots: { '1x1': EMPTY_SLOT, '4x5': { kind: 'new', file: files[fileIdx++] } },
          headline: '',
          description: '',
        });
      }
      return next;
    });
  }

  useEffect(() => {
    if (injectedFiles4x5 && injectedFiles4x5.length > 0) addBulkFiles4x5(injectedFiles4x5);
  }, [injectedFiles4x5]); // addBulkFiles4x5 is stable

  function onZoneDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(true);
  }
  function onZoneDragLeave(e: React.DragEvent) {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingFile(false);
  }
  function onZoneDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(false);
    addBulkFiles(e.dataTransfer.files);
  }

  // Merged so a pair-level issue ("1:1 or 4:5 missing") surfaces on the section.
  const sectionIssue = mergeIssues(sectionIssueKey, fieldIssues);

  return (
    <div>
      {/* Section header — click to collapse */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full mb-1.5 group/sec"
      >
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
          {label}
          <span className="normal-case font-normal tracking-normal ml-1">
            ({cards.length} / {MAX_CARDS})
          </span>
        </p>
        <Chevron
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className="border-b border-slate-200 mb-3" />

      {open && (
        <div
          onDragOver={onZoneDragOver}
          onDragLeave={onZoneDragLeave}
          onDrop={onZoneDrop}
          className={`rounded-xl transition-all ${isDraggingFile ? 'ring-2 ring-[#0014cc]/30 bg-[#0014cc]/[0.02]' : ''}`}
        >
          <input
            ref={bulkInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                addBulkFiles(e.target.files);
                e.target.value = '';
              }
            }}
          />

          <div className="space-y-2">
            {cards.map((card, i) => (
              <CardRow
                key={card.id}
                card={card}
                index={i}
                total={cards.length}
                isActive={i === activeCardIndex}
                activeRatio={activeRatio}
                headlineIssue={fieldIssues[`headline.${i}`] ?? fieldIssues['headline']}
                cardImageIssue={fieldIssues[`card_image.${i}`]}
                descriptionIssue={fieldIssues[`description.${i}`] ?? fieldIssues['description']}
                showErrors={showErrors}
                showWarnings={showWarnings}
                feedback={feedback}
                feedbackPending={feedbackPending}
                onFeedback={onFeedback}
                onSelect={(r) => onSelectCard(i, r)}
                onSlotFileChange={(r, f) => handleSlotFileChange(card, r, f)}
                onHeadlineChange={(v) => updateCard(card.id, { headline: v })}
                onDescriptionChange={(v) => updateCard(card.id, { description: v })}
                onMoveUp={() => moveCard(i, -1)}
                onMoveDown={() => moveCard(i, 1)}
                onRemove={() => removeCard(card)}
              />
            ))}
          </div>

          {cards.length < MAX_CARDS && (
            <button
              type="button"
              onClick={() => bulkInputRef.current?.click()}
              className={`mt-2 w-full py-2.5 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 transition-all ${
                isDraggingFile
                  ? 'border-[#0014cc]/50 bg-[#0014cc]/5 text-[#0014cc]'
                  : sectionIssue?.errors.length && showErrors
                    ? 'border-red-300 hover:border-red-400 text-red-400 hover:text-red-500 hover:bg-red-50'
                    : sectionIssue?.warnings.length && showWarnings
                      ? 'border-amber-300 hover:border-amber-400 text-amber-400 hover:text-amber-500 hover:bg-amber-50'
                      : 'border-slate-200 hover:border-slate-300 text-slate-400 hover:text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs font-medium">
                {isDraggingFile ? 'Drop files to add cards' : 'Add card or drop files here'}
              </span>
            </button>
          )}

          {/* Section-level count validation (e.g. "Feed (1:1) - less than 3 images") */}
          <InlineIssue
            issue={sectionIssue}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onFeedback}
          />
        </div>
      )}
    </div>
  );
}

// ─── Story card section (9:16 — image-only, Snapchat-style thumbnails) ────────

interface StoryNewCard {
  id: number;
  kind: 'new';
  file: File;
}
interface StoryExistingCard {
  id: number;
  kind: 'existing';
  existingAssetId: string;
  previewUrl: string;
  contentType?: string | null;
}
type StoryCard = StoryNewCard | StoryExistingCard;

let storyNextId = 100_000;

function StoryThumb({
  card,
  index,
  isActive,
  isDragging,
  isDragOver,
  onSelect,
  onRemove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  card: StoryCard;
  index: number;
  isActive: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}) {
  const [blobSrc, setBlobSrc] = useState<string | null>(null);
  useEffect(() => {
    if (card.kind !== 'new') return;
    const url = URL.createObjectURL(card.file);
    setBlobSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [card]);

  const src = card.kind === 'existing' ? card.previewUrl : blobSrc;
  const isVideo =
    card.kind === 'new'
      ? card.file.type.startsWith('video/')
      : (card.contentType?.startsWith('video/') ?? false);

  if (!src) return null;

  return (
    <div
      draggable
      onClick={onSelect}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`relative flex-shrink-0 h-36 rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all group/thumb ${
        isDragging ? 'opacity-40' : ''
      } ${
        isDragOver
          ? 'border-slate-400 scale-105'
          : isActive
            ? 'border-[#0014cc]'
            : 'border-slate-200 hover:border-slate-300'
      }`}
      style={{ aspectRatio: '9/16' }}
    >
      {isVideo ? (
        <video
          src={src}
          muted
          playsInline
          preload="auto"
          onLoadedData={(e) => {
            e.currentTarget.currentTime = 0.1;
          }}
          className="w-full h-full object-cover"
        />
      ) : (
        <img src={src} alt={`Slide ${index + 1}`} className="w-full h-full object-cover" />
      )}
      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover/thumb:opacity-0 transition-opacity">
          <div className="w-7 h-7 rounded-full bg-black/50 flex items-center justify-center">
            <Play className="w-3 h-3 text-white fill-white ml-0.5" />
          </div>
        </div>
      )}
      <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center bg-black/25 text-white">
        {index + 1}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow opacity-0 group-hover/thumb:opacity-100 transition-opacity"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function StoryCardSection({
  activeCardIndex,
  fieldIssues,
  showErrors,
  showWarnings,
  sectionIssueKey,
  feedback,
  feedbackPending,
  onFeedback,
  onItemsChange,
  onCardsChange,
  onSelectCard,
  initialItems,
  injectedFiles,
}: {
  activeCardIndex: number;
  fieldIssues: Record<string, FieldIssue>;
  showErrors: boolean;
  showWarnings: boolean;
  sectionIssueKey: string;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onItemsChange: (items: { imageUrl?: string }[]) => void;
  onCardsChange: (
    newFiles: File[],
    deletedIds: string[],
    totalCount: number,
    cardData: { headline: string; description: string }[],
    order: AssetOrderToken[],
  ) => void;
  onSelectCard: (index: number) => void;
  initialItems?: {
    imageUrl: string;
    existingAssetId: string;
    contentType?: string | null;
  }[];
  /** Files injected from outside (e.g., GX import). Added to cards when changed. */
  injectedFiles?: File[];
}) {
  const [cards, setCards] = useState<StoryCard[]>([]);
  // Identity of the initial empty array, used to detect the pre-seed render.
  const initialCardsRef = useRef(cards);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const addMoreInputRef = useRef<HTMLInputElement>(null);
  const deletedIdsRef = useRef<string[]>([]);
  // Signature of the initialItems last seeded from — see seedSignature.ts.
  const seededSigRef = useRef<string | null>(null);
  // Cache blob URLs by card ID so notify() returns the same URL on every call
  const blobUrlCacheRef = useRef<Map<number, string>>(new Map());
  // Ref that always holds the latest cards so notify() reads fresh data without depending on cards identity
  const storyCardsRef = useRef(cards);
  storyCardsRef.current = cards;
  // Ref for initialItems so notify can check seeding status without depending on prop identity
  const storyInitialItemsRef = useRef(initialItems);
  storyInitialItemsRef.current = initialItems;

  // Seed (and re-seed) cards from the DB whenever the incoming asset identities change
  useEffect(() => {
    const sig = seedSignature(initialItems);
    if (!sig || sig === seededSigRef.current) return;
    seededSigRef.current = sig;
    // A re-seed adopts server state wholesale, so pending deletions from the
    // previous seed no longer apply — they are already reflected in initialItems.
    deletedIdsRef.current = [];
    // Previous local previews are dropped wholesale by the re-seed below; their
    // cached blob URLs are no longer reachable for per-card cleanup.
    revokeCachedBlobUrls(blobUrlCacheRef.current);
    setCards(
      initialItems!.map((item) => ({
        id: storyNextId++,
        kind: 'existing' as const,
        existingAssetId: item.existingAssetId,
        previewUrl: item.imageUrl,
        contentType: item.contentType,
      })),
    );
  }, [initialItems]);

  const notify = useCallback(() => {
    const currentCards = storyCardsRef.current;
    onItemsChange(
      currentCards.map((c) => {
        if (c.kind === 'existing') return { imageUrl: c.previewUrl, contentType: c.contentType };
        // Cache blob URL per card ID so the same URL string is returned every call (prevents video flicker)
        if (!blobUrlCacheRef.current.has(c.id)) {
          blobUrlCacheRef.current.set(c.id, createMediaBlobUrl(c.file));
        }
        return {
          imageUrl: blobUrlCacheRef.current.get(c.id),
          contentType: c.file.type,
        };
      }),
    );
    // Don't push empty defaults to parent while waiting for DB cards to seed.
    // Identity check rather than the seed signature: the seeding effect runs
    // first on mount and records the signature before its setCards is applied.
    if (storyInitialItemsRef.current?.length && currentCards === initialCardsRef.current) return;
    const newFiles = currentCards
      .filter((c): c is StoryNewCard => c.kind === 'new')
      .map((c) => c.file);
    let storyNewIdx = 0;
    const order: AssetOrderToken[] = currentCards.map((c) =>
      c.kind === 'existing'
        ? { existingAssetId: c.existingAssetId }
        : { newFileIndex: storyNewIdx++ },
    );
    onCardsChange(
      newFiles,
      deletedIdsRef.current,
      currentCards.length,
      currentCards.map(() => ({ headline: '', description: '' })),
      order,
    );
  }, [onItemsChange, onCardsChange]);

  // Re-notify parent whenever cards state changes (notify reads fresh data via storyCardsRef)
  useEffect(() => {
    notify();
  }, [cards, notify]);

  function addFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    const remaining = MAX_CARDS - cards.length;
    if (remaining <= 0) return;
    setCards((prev) => [
      ...prev,
      ...files
        .slice(0, remaining)
        .map((f) => ({ id: storyNextId++, kind: 'new' as const, file: f })),
    ]);
  }

  useEffect(() => {
    if (injectedFiles && injectedFiles.length > 0) addFiles(injectedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectedFiles]);

  function removeCard(card: StoryCard) {
    if (card.kind === 'existing')
      deletedIdsRef.current = [...deletedIdsRef.current, card.existingAssetId];
    const cached = blobUrlCacheRef.current.get(card.id);
    if (cached) {
      URL.revokeObjectURL(cached);
      blobUrlCacheRef.current.delete(card.id);
    }
    setCards((prev) => prev.filter((c) => c.id !== card.id));
  }

  function reorder(from: number, to: number) {
    if (from === to) return;
    const next = [...cards];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setCards(next);
  }

  const sectionIssue = fieldIssues[sectionIssueKey];
  const hasCards = cards.length > 0;
  const atMax = cards.length >= MAX_CARDS;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <button
          type="button"
          className="text-[11px] font-semibold uppercase tracking-wider text-slate-400"
        >
          Story Cards · 9:16
          <span className="normal-case font-normal tracking-normal ml-1">
            ({cards.length} / {MAX_CARDS})
          </span>
        </button>
        {hasCards && (
          <p className="text-[11px] text-slate-400">Drag to reorder · Click to preview</p>
        )}
      </div>
      <div className="border-b border-slate-200 mb-3" />

      {!hasCards ? (
        <button
          type="button"
          onClick={() => bulkInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (dragIndex === null) setIsDraggingFile(true);
          }}
          onDragLeave={() => setIsDraggingFile(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingFile(false);
            if (dragIndex === null) addFiles(e.dataTransfer.files);
          }}
          className={`h-36 w-full rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2 px-4 ${
            isDraggingFile
              ? 'border-[#0014cc]/50 bg-[#0014cc]/5'
              : sectionIssue?.errors.length && showErrors
                ? 'border-red-400 bg-red-50/20 hover:border-red-500'
                : sectionIssue?.warnings.length && showWarnings
                  ? 'border-amber-400 bg-amber-50/20 hover:border-amber-500'
                  : 'border-dashed border-slate-200 hover:border-slate-300 hover:bg-slate-50'
          }`}
        >
          <input
            ref={bulkInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
            }}
          />
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${isDraggingFile ? 'bg-[#0014cc]/10' : 'bg-slate-100'}`}
          >
            <Upload
              className={`w-4 h-4 transition-colors ${isDraggingFile ? 'text-[#0014cc]' : 'text-slate-400'}`}
            />
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 font-medium">
              {isDraggingFile ? 'Drop to upload' : 'Click to upload or drag & drop'}
            </p>
            {!isDraggingFile && (
              <p className="text-[11px] text-slate-400 mt-0.5">
                Select multiple 9:16 files at once
              </p>
            )}
          </div>
        </button>
      ) : (
        <div className="overflow-x-auto min-w-0 max-w-full">
          <div className="flex gap-2" style={{ width: 'max-content' }}>
            {cards.map((card, i) => (
              <StoryThumb
                key={card.id}
                card={card}
                index={i}
                isActive={i === activeCardIndex}
                isDragging={dragIndex === i}
                isDragOver={dragOverIndex === i && dragIndex !== null && dragIndex !== i}
                onSelect={() => onSelectCard(i)}
                onRemove={() => removeCard(card)}
                onDragStart={() => {
                  setDragIndex(i);
                  setIsDraggingFile(false);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (dragIndex !== null) setDragOverIndex(i);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (dragIndex !== null) reorder(dragIndex, i);
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
              />
            ))}
            {!atMax && (
              <button
                type="button"
                onClick={() => addMoreInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragIndex === null) setIsDraggingFile(true);
                }}
                onDragLeave={() => setIsDraggingFile(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingFile(false);
                  if (dragIndex === null) addFiles(e.dataTransfer.files);
                }}
                className={`relative flex-shrink-0 h-36 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all ${
                  isDraggingFile
                    ? 'border-[#0014cc]/50 bg-[#0014cc]/5'
                    : sectionIssue?.errors.length && showErrors
                      ? 'border-red-400 bg-red-50/20 hover:border-red-500'
                      : sectionIssue?.warnings.length && showWarnings
                        ? 'border-amber-400 bg-amber-50/20 hover:border-amber-500'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
                style={{ aspectRatio: '9/16' }}
              >
                <Plus
                  className={`w-5 h-5 transition-colors ${isDraggingFile ? 'text-[#0014cc]' : 'text-slate-400'}`}
                />
                <span className="text-[10px] font-medium text-slate-400">Add</span>
                <input
                  ref={addMoreInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) addFiles(e.target.files);
                  }}
                />
              </button>
            )}
          </div>
        </div>
      )}
      <InlineIssue
        issue={sectionIssue}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onFeedback}
      />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export type MetaCarouselCardData = { headline: string; description: string };
/** Per-crop upload payload for the feed section. */
export type MetaCarouselFeedUploads = Record<
  FeedRatio,
  { newFiles: File[]; order: AssetOrderToken[] }
>;
export type MetaCarouselInitialItem = {
  imageUrl?: string;
  existingAssetId?: string;
  contentType?: string | null;
  /** The card's 4:5 crop, if one was uploaded. Shares this card's text. */
  imageUrl4x5?: string;
  existingAssetId4x5?: string;
  contentType4x5?: string | null;
  headline: string;
  description: string;
};

export interface MetaCarouselDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  caption: string;
  onCaptionChange: (v: string) => void;
  /** Shared destination URL for all carousel cards */
  destinationUrl: string;
  onDestinationUrlChange: (v: string) => void;
  /** Shared CTA for all carousel cards */
  ctaValue: string;
  onCtaChange: (v: string) => void;
  /** Lifted to parent so save handler can include it in the payload */
  legal: string;
  onLegalChange: (v: string) => void;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  activeFeedCardIndex: number;
  activeStoryCardIndex: number;
  /** Which ratio is currently shown in the mockup — drives which section highlights */
  activeRatio: '1x1' | '4x5' | '9x16';
  onFeedItemsChange: (
    items: {
      imageUrl?: string;
      contentType?: string | null;
      imageUrl4x5?: string;
      contentType4x5?: string | null;
      headline?: string;
      description?: string;
    }[],
  ) => void;
  onStoryItemsChange: (items: { imageUrl?: string; contentType?: string | null }[]) => void;
  /** Selecting a feed card also picks which of its crops the mockup shows. */
  onSelectFeedCard: (index: number, ratio: FeedRatio) => void;
  onSelectStoryCard: (index: number) => void;
  /** For save: per-crop uploads plus the shared card list — feed (1:1 and 4:5) */
  onFeedCardsChange: (
    uploads: MetaCarouselFeedUploads,
    deletedIds: string[],
    totalCount: number,
    cardData: MetaCarouselCardData[],
  ) => void;
  /** For save: new files, deleted asset IDs, total count, per-card data — story (9:16) */
  onStoryCardsChange: (
    newFiles: File[],
    deletedIds: string[],
    totalCount: number,
    cardData: MetaCarouselCardData[],
    order: AssetOrderToken[],
  ) => void;
  /** Existing feed cards from DB — resolved image URLs and per-card metadata */
  initialFeedItems?: MetaCarouselInitialItem[];
  /** Existing story cards from DB — image URLs only (no per-card text for 9:16) */
  initialStoryItems?: {
    imageUrl: string;
    existingAssetId: string;
    contentType?: string | null;
  }[];
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

/**
 * How many images one feed crop currently holds, for Gear Exchange zone limits.
 *
 * The 1:1 and 4:5 crops have separate GX zones but deletions arrive as a single
 * flat id list covering both, so each crop must count only the deleted ids that
 * were its own. Subtracting the whole list from both crops made deleting a 4:5
 * shrink the 1:1 occupancy too, which let GX overfill the 1:1 zone.
 */
export function cropOccupancy(
  newFileCount: number,
  initialCount: number,
  deletedIds: string[],
  ownIds: ReadonlySet<string>,
): number {
  const deletedOwn = deletedIds.filter((id) => ownIds.has(id)).length;
  return newFileCount + (initialCount - deletedOwn);
}

export function MetaCarouselDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  caption,
  onCaptionChange,
  destinationUrl,
  onDestinationUrlChange,
  ctaValue,
  onCtaChange,
  legal,
  onLegalChange,
  feedback,
  feedbackPending,
  onAIFeedback,
  activeFeedCardIndex,
  activeStoryCardIndex,
  activeRatio,
  onFeedItemsChange,
  onStoryItemsChange,
  onSelectFeedCard,
  onSelectStoryCard,
  onFeedCardsChange,
  onStoryCardsChange,
  initialFeedItems,
  initialStoryItems,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: MetaCarouselDetailsProps) {
  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);
  const [gxInjectedFeed, setGxInjectedFeed] = useState<File[]>();
  const [gxInjectedFeed4x5, setGxInjectedFeed4x5] = useState<File[]>();
  const [gxInjectedStory, setGxInjectedStory] = useState<File[]>();
  // Track initial *image* count separately — text-only cards (no imageUrl/existingAssetId)
  // should not count toward image-based limits (e.g. Gear Exchange zoneLimits).
  const initialFeedImageCount =
    initialFeedItems?.filter((i) => i.imageUrl || i.existingAssetId).length ?? 0;
  const initialFeed4x5Count =
    initialFeedItems?.filter((i) => i.imageUrl4x5 || i.existingAssetId4x5).length ?? 0;
  const initialStoryImageCount =
    initialStoryItems?.filter((i) => i.imageUrl || i.existingAssetId).length ?? 0;

  const [feedCardCount, setFeedCardCount] = useState(initialFeedImageCount);
  const [feed4x5Count, setFeed4x5Count] = useState(initialFeed4x5Count);
  const [storyCardCount, setStoryCardCount] = useState(initialStoryImageCount);

  // Refs for initial item lengths so the stable callbacks below don't depend on prop identity
  const initialFeedLengthRef = useRef(initialFeedImageCount);
  initialFeedLengthRef.current = initialFeedImageCount;
  const initialFeed4x5LengthRef = useRef(initialFeed4x5Count);
  initialFeed4x5LengthRef.current = initialFeed4x5Count;

  // The two feed crops have separate GX zones, but deletions arrive as one flat
  // id list covering both. These sets say which crop an id belonged to, so a
  // deleted 4:5 doesn't also decrement the 1:1 occupancy (which would let Gear
  // Exchange overfill the 1:1 zone).
  const initialFeed1x1IdsRef = useRef<Set<string>>(new Set());
  initialFeed1x1IdsRef.current = new Set(
    initialFeedItems?.map((i) => i.existingAssetId).filter((id): id is string => !!id) ?? [],
  );
  const initialFeed4x5IdsRef = useRef<Set<string>>(new Set());
  initialFeed4x5IdsRef.current = new Set(
    initialFeedItems?.map((i) => i.existingAssetId4x5).filter((id): id is string => !!id) ?? [],
  );
  const initialStoryLengthRef = useRef(initialStoryImageCount);
  initialStoryLengthRef.current = initialStoryImageCount;

  // Stable wrappers for CardSection onCardsChange (updates local card count + forwards to parent)
  const handleFeedCardsChange = useCallback(
    (
      uploads: MetaCarouselFeedUploads,
      deletedIds: string[],
      totalCount: number,
      cardData: { headline: string; description: string }[],
    ) => {
      // Each crop counts only the deletions that belong to it — deletedIds is a
      // single list spanning both, so filter by crop before subtracting.
      setFeedCardCount(
        cropOccupancy(
          uploads['1x1'].newFiles.length,
          initialFeedLengthRef.current,
          deletedIds,
          initialFeed1x1IdsRef.current,
        ),
      );
      setFeed4x5Count(
        cropOccupancy(
          uploads['4x5'].newFiles.length,
          initialFeed4x5LengthRef.current,
          deletedIds,
          initialFeed4x5IdsRef.current,
        ),
      );
      onFeedCardsChange(uploads, deletedIds, totalCount, cardData);
    },
    [onFeedCardsChange],
  );
  const handleStoryCardsChange = useCallback(
    (
      newFiles: File[],
      deletedIds: string[],
      totalCount: number,
      cardData: { headline: string; description: string }[],
      order: AssetOrderToken[],
    ) => {
      setStoryCardCount(newFiles.length + (initialStoryLengthRef.current - deletedIds.length));
      onStoryCardsChange(newFiles, deletedIds, totalCount, cardData, order);
    },
    [onStoryCardsChange],
  );

  return (
    <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0 min-w-0">
      {/* ── About ───────────────────────────────────────────────────── */}
      <div className="border-l-2 border-[#0014cc]/30 pl-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          About this format
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          A <span className="font-medium text-slate-700">horizontally scrollable carousel</span> of
          2–10 cards. Upload <span className="font-medium text-slate-700">1:1</span> cards (with
          headline + description) for Facebook and Audience Network, and{' '}
          <span className="font-medium text-slate-700">9:16</span> images for Instagram Stories —
          Meta automatically serves each to the right placement.{' '}
          <a
            href="https://www.facebook.com/business/help/1114358518575630?id=563305920700338"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
          >
            Meta Carousel specs ↗
          </a>
        </p>
      </div>

      {
        <>
          <GearExchangeImportButton
            totalAssets={gxData?.total_assets ?? null}
            loading={gxLoading}
            error={gxError}
            fordshareIds={fordshareIds ?? undefined}
            onClick={() => setShowGxModal(true)}
          />

          {showGxModal && gxData && campaign && campaignId && (
            <GearExchangeModal
              data={gxData}
              campaign={campaign}
              campaignId={campaignId}
              formatLabel="Meta Carousel"
              zoneLimits={[
                { ratio: '1x1', limit: 10, existing: feedCardCount },
                { ratio: '4x5', limit: 10, existing: feed4x5Count },
                { ratio: '9x16', limit: 10, existing: storyCardCount },
              ]}
              onClose={() => setShowGxModal(false)}
              usedInVariants={usedInVariants}
              onImport={(files: GXImportedFile[]) => {
                const feed = files.filter((f) => f.ratio === '1x1').map((f) => f.file);
                const feed4x5 = files.filter((f) => f.ratio === '4x5').map((f) => f.file);
                const story = files.filter((f) => f.ratio === '9x16').map((f) => f.file);
                if (feed.length > 0) setGxInjectedFeed(feed);
                if (feed4x5.length > 0) setGxInjectedFeed4x5(feed4x5);
                if (story.length > 0) setGxInjectedStory(story);
              }}
            />
          )}
        </>
      }

      {/* ── Primary Text ────────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Primary Text"
          right={<CharCount value={caption} limit={PRIMARY_TEXT_LIMIT} />}
          tooltip="Shared post text shown above the carousel. Max 125 chars."
        />
        <textarea
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          placeholder="Write something that will make your audience stop scrolling..."
          rows={3}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['primary_text'], showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={fieldIssues['primary_text']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Feed Cards (each with a 1:1 and a 4:5 crop) ──────────────── */}
      <CardSection
        label="Feed Cards"
        activeCardIndex={activeFeedCardIndex}
        // The story ratio never reaches this section; fall back to 1:1 so the
        // prop stays a FeedRatio while the story section is the active one.
        activeRatio={activeRatio === '9x16' ? '1x1' : activeRatio}
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        // Every crop-related count issue lights up the one section, since the crops
        // are two renderings of the same cards. `feed` carries the "not enough
        // cards" error (it counts distinct card positions, so a card with both
        // crops still counts once); `4x5` carries the "also provide a 4:5"
        // recommendation; `1x1` is kept for issues saved before the split.
        sectionIssueKey={['ratio_items.feed', 'ratio_items.1x1', 'ratio_items.4x5']}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onItemsChange={onFeedItemsChange}
        onCardsChange={handleFeedCardsChange}
        onSelectCard={onSelectFeedCard}
        initialItems={initialFeedItems}
        injectedFiles={gxInjectedFeed}
        injectedFiles4x5={gxInjectedFeed4x5}
      />

      {/* ── 9:16 Cards (Stories) ────────────────────────────────────── */}
      <StoryCardSection
        activeCardIndex={activeStoryCardIndex}
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        sectionIssueKey="ratio_items.9x16"
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onItemsChange={onStoryItemsChange}
        onCardsChange={handleStoryCardsChange}
        onSelectCard={onSelectStoryCard}
        initialItems={initialStoryItems}
        injectedFiles={gxInjectedStory}
      />

      {/* ── Destination URL ─────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Destination URL"
          tooltip="Shared landing page URL for all carousel cards."
        />
        <input
          type="url"
          value={destinationUrl}
          onChange={(e) => onDestinationUrlChange(e.target.value)}
          placeholder="https://www.ford.com/..."
          className={`${inputClass} ${issueBorder(fieldIssues['destination_url'], showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={fieldIssues['destination_url']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Call to Action"
          tooltip="Shared CTA button text for all carousel cards."
        />
        <select
          value={ctaValue}
          onChange={(e) => onCtaChange(e.target.value)}
          className={`${inputClass} cursor-pointer`}
        >
          {CTA_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      {/* ── Legal ───────────────────────────────────────────────────── */}
      <Field
        label="Legal"
        tooltip="Required in some markets (e.g. France) for regulated categories. Internal only — not displayed in the mockup."
        fieldKey="legal"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
      >
        <textarea
          value={legal}
          onChange={(e) => onLegalChange(e.target.value)}
          placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
          rows={5}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
        />
      </Field>
    </div>
  );
}
